import React, { useState, useEffect } from 'react';
import { X, Save, Calendar, DollarSign, User, Users, FileText, Download, Building, Plus, Trash2, Link, Mail, Linkedin, RefreshCw, Archive, History, AlertTriangle, ClipboardList, Film, MessageSquare, FileSignature, ExternalLink, Eye, Send, CheckCircle, CheckSquare, Square, Clock, CalendarIcon, AlertCircle, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Calendar as CalendarComponent } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import MediaChecklist from './MediaChecklist';
import OperationsChecklist from './OperationsChecklist';
import { useTheme } from '../contexts/ThemeContext';
import { sanitizeHTML } from '../lib/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Columns where retirement is allowed
const RETIREMENT_ALLOWED_COLUMNS = ['New Lead', 'Prospect', 'Qualified', 'Proposal Sent', 'Renewal Due'];

// Helper function to download files with authentication
const downloadFileWithAuth = async (url, filename) => {
  try {
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Download failed');
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download error:', error);
    alert('Failed to download file');
  }
};

// Contact card component for reusability
const ContactCard = ({ label, firstNameValue, lastNameValue, titleValue, emailValue, phoneValue, linkedinValue, onFirstNameChange, onLastNameChange, onTitleChange, onEmailChange, onPhoneChange, onLinkedinChange, isDark }) => {
  const linkedinInputRef = React.useRef(null);
  
  const handleLinkedinIconClick = (e) => {
    e.preventDefault();
    if (linkedinValue) {
      window.open(linkedinValue, '_blank', 'noopener,noreferrer');
    } else if (linkedinInputRef.current) {
      linkedinInputRef.current.focus();
    }
  };
  
  return (
    <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-4`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'}`}>{label}</h4>
        {/* LinkedIn Icon - blue if URL present, white/gray if empty (clickable to add) */}
        <button
          onClick={handleLinkedinIconClick}
          className={`p-1.5 rounded transition-colors ${
            linkedinValue 
              ? 'bg-[#0077B5] text-white hover:bg-[#005885]' 
              : 'bg-white/10 text-gray-400 hover:text-white hover:bg-white/20'
          }`}
          title={linkedinValue ? 'Open LinkedIn Profile' : 'Click to add LinkedIn URL'}
        >
          <Linkedin className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">First Name</label>
            <input
              type="text"
              value={firstNameValue || ''}
              onChange={(e) => onFirstNameChange(e.target.value)}
              className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
              placeholder="First name"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Last Name</label>
            <input
              type="text"
              value={lastNameValue || ''}
              onChange={(e) => onLastNameChange(e.target.value)}
              className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
              placeholder="Last name"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
          <input
            type="text"
            value={titleValue || ''}
            onChange={(e) => onTitleChange(e.target.value)}
            className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
            placeholder="Job title"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
            <input
              type="email"
              value={emailValue || ''}
              onChange={(e) => onEmailChange(e.target.value)}
              className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
              placeholder="email@company.com"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
            <input
              type="tel"
              value={phoneValue || ''}
              onChange={(e) => onPhoneChange(e.target.value)}
              className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
              placeholder="07123 456789"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
            <Linkedin className="w-3 h-3" /> LinkedIn URL
          </label>
          <input
            ref={linkedinInputRef}
            type="url"
            value={linkedinValue || ''}
            onChange={(e) => onLinkedinChange(e.target.value)}
            className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
            placeholder="https://linkedin.com/in/username"
          />
        </div>
      </div>
    </div>
  );
};

// Email Note Card Component - displays synced email with expand/collapse
const EmailNoteCard = ({ email, isIncoming, isDark }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Parse sender name from "Name <email>" format
  const senderMatch = email.from_address?.match(/^(.+?)\s*<(.+?)>$/) || [];
  const senderName = senderMatch[1]?.replace(/"/g, '').trim() || email.from_address?.split('@')[0] || 'Unknown';
  const senderEmail = senderMatch[2] || email.from_address || '';
  
  // Parse recipient - handle multiple recipients
  const toAddresses = email.to_address?.split(',') || [];
  const firstRecipient = toAddresses[0]?.trim() || '';
  const recipientMatch = firstRecipient.match(/^(.+?)\s*<(.+?)>$/) || [];
  const recipientName = recipientMatch[1]?.replace(/"/g, '').trim() || firstRecipient.split('@')[0] || 'Unknown';
  const recipientEmail = recipientMatch[2] || firstRecipient || '';
  const hasMoreRecipients = toAddresses.length > 1;
  
  return (
    <div 
      className={`${isDark ? 'bg-[#0A0A0A] border-blue-500/30' : 'bg-blue-50 border-blue-200'} border-l-4 rounded-sm p-4 cursor-pointer transition-all`}
      onClick={() => setExpanded(!expanded)}
    >
      {/* Header with date and tag */}
      <div className="flex items-center justify-between gap-4 mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-full ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'} flex items-center justify-center`}>
            <Mail className="w-3 h-3 text-blue-500" />
          </div>
          <span className={`text-xs px-2 py-0.5 ${isIncoming ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'} rounded`}>
            {isIncoming ? 'Received' : 'Sent'}
          </span>
        </div>
        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} flex-shrink-0`}>
          {email.date ? new Date(email.date).toLocaleString() : 'Unknown date'}
        </span>
      </div>
      
      {/* From/To display - always visible */}
      <div className={`ml-8 mb-2 text-sm space-y-1`}>
        <div className="flex items-center gap-2">
          <span className={`font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} w-12`}>From:</span>
          <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{senderName}</span>
          <span className={`${isDark ? 'text-gray-500' : 'text-gray-400'} text-xs`}>&lt;{senderEmail}&gt;</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} w-12`}>To:</span>
          <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{recipientName}</span>
          <span className={`${isDark ? 'text-gray-500' : 'text-gray-400'} text-xs`}>&lt;{recipientEmail}&gt;</span>
          {hasMoreRecipients && (
            <span className={`${isDark ? 'text-gray-500' : 'text-gray-400'} text-xs`}>+{toAddresses.length - 1} more</span>
          )}
        </div>
        {email.cc_address && (
          <div className="flex items-center gap-2">
            <span className={`font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} w-12`}>CC:</span>
            <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-xs truncate`}>{email.cc_address}</span>
          </div>
        )}
      </div>
      
      {/* Subject and body */}
      <div className="ml-8">
        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>{email.subject || '(No Subject)'}</p>
        {expanded ? (
          <div className={`${isDark ? 'text-gray-300' : 'text-gray-700'} text-sm whitespace-pre-wrap mt-2 p-3 ${isDark ? 'bg-[#111]' : 'bg-white'} rounded border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            {email.body_snippet}
          </div>
        ) : (
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm line-clamp-2`}>
            {email.body_snippet?.substring(0, 150)}...
          </p>
        )}
        <p className={`text-xs mt-2 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
          {expanded ? 'Click to collapse' : 'Click to expand'}
        </p>
      </div>
    </div>
  );
};

const CardDetailModal = ({ card, isOpen, onClose, onUpdate, boardType, columnName, defaultTab = 'details' }) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [editedCard, setEditedCard] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showRetireModal, setShowRetireModal] = useState(false);
  const [retireReason, setRetireReason] = useState('');
  const [retiring, setRetiring] = useState(false);
  const [reinstating, setReinstating] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignUserId, setReassignUserId] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const [cardHistory, setCardHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [cardNotes, setCardNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [cardProposals, setCardProposals] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [proposalTemplates, setProposalTemplates] = useState([]);
  const [creatingProposal, setCreatingProposal] = useState(false);
  // Synced emails state (from batch sync job)
  const [syncedEmails, setSyncedEmails] = useState([]);
  const [loadingSyncedEmails, setLoadingSyncedEmails] = useState(false);
  // Industry search state
  const [industrySearch, setIndustrySearch] = useState('');
  const [industryResults, setIndustryResults] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(false);
  // Tasks state
  const [cardTasks, setCardTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [newTaskData, setNewTaskData] = useState({
    title: '',
    task_type: 'other',
    priority: 'medium',
    description: '',
    due_date: '',
    due_time: '',
    assigned_to_id: ''
  });
  const [savingTask, setSavingTask] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // Speaker invite modal state
  const [showSpeakerInviteModal, setShowSpeakerInviteModal] = useState(false);
  const [speakerInvitePreview, setSpeakerInvitePreview] = useState(null);
  const [speakerInviteLoading, setSpeakerInviteLoading] = useState(false);
  const [speakerInviteSending, setSpeakerInviteSending] = useState(false);
  const [selectedSpeakers, setSelectedSpeakers] = useState([]);
  
  // Track the previous card_id to detect when a NEW card is opened vs same card updated
  const [previousCardId, setPreviousCardId] = useState(null);

  useEffect(() => {
    if (card) {
      const isNewCard = card.card_id !== previousCardId;
      
      if (isNewCard) {
        // Only reset these when opening a NEW card, not when same card updates
        setIsInitialLoad(true);
        setActiveTab(defaultTab);
        setIndustrySearch(card.industry || '');
        setIndustryResults([]);
        setSaveStatus('idle');
        fetchCardNotes(card.card_id);
        fetchCardTasks(card.card_id);
        fetchCurrentUser();
        fetchUsers();
        setPreviousCardId(card.card_id);
        setTimeout(() => setIsInitialLoad(false), 500);
      }
      
      // Always update editedCard with latest card data
      const cardWithDefaults = {
        ...card,
        speakers: card.speakers || [],
        key_people: card.key_people || [],
        relationship_status: card.relationship_status || card.contract_status || 'suspect'
      };
      setEditedCard(cardWithDefaults);
    } else {
      // Card closed - reset previousCardId
      setPreviousCardId(null);
    }
  }, [card]);

  // Auto-save with debounce
  useEffect(() => {
    if (!editedCard || !card || isInitialLoad) return;
    
    // Check if there are actual changes
    const hasChanges = JSON.stringify(editedCard) !== JSON.stringify({
      ...card,
      speakers: card.speakers || [],
      key_people: card.key_people || [],
      relationship_status: card.relationship_status || card.contract_status || 'suspect'
    });
    
    if (!hasChanges) return;
    
    setSaveStatus('saving');
    
    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cards/${card.card_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            title: editedCard.title,
            description: editedCard.description,
            relationship_status: editedCard.relationship_status,
            contract_date: editedCard.contract_date,
            churn_date: editedCard.churn_date,
            event_date: editedCard.event_date,
            customer_bio: editedCard.customer_bio,
            speakers: editedCard.speakers,
            key_people: editedCard.key_people,
            color_pinned: editedCard.color_pinned,
            company_address: editedCard.company_address,
            city_town: editedCard.city_town,
            zip_code: editedCard.zip_code,
            country: editedCard.country,
            company_phone: editedCard.company_phone,
            website_url: editedCard.website_url,
            company_registration: editedCard.company_registration,
            industry: editedCard.industry,
            relationship_owner_id: editedCard.relationship_owner_id
          })
        });

        if (response.ok) {
          setSaveStatus('saved');
          onUpdate();
          // Reset to idle after showing "Saved" for a moment
          setTimeout(() => setSaveStatus('idle'), 2000);
        } else {
          setSaveStatus('error');
        }
      } catch (error) {
        console.error('Auto-save error:', error);
        setSaveStatus('error');
      }
    }, 1000); // 1 second debounce

    return () => clearTimeout(timeoutId);
  }, [editedCard]);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const user = await response.json();
        setCurrentUser(user);
      }
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, { credentials: 'include' });
      if (response.ok) {
        const allUsers = await response.json();
        setUsers(allUsers.filter(u => u.is_active));
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchCardNotes = async (cardId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cards/${cardId}/notes`, { credentials: 'include' });
      if (response.ok) {
        const notes = await response.json();
        setCardNotes(notes);
      }
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    }
  };

  // Fetch tasks for the card
  const fetchCardTasks = async (cardId) => {
    setLoadingTasks(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks/card/${cardId}`, { credentials: 'include' });
      if (response.ok) {
        const tasks = await response.json();
        setCardTasks(tasks);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  // Industry search with debounce
  const searchIndustries = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setIndustryResults([]);
      return;
    }
    setLoadingIndustries(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/industries?search=${encodeURIComponent(searchTerm)}`);
      if (response.ok) {
        const results = await response.json();
        setIndustryResults(results.slice(0, 10));
      }
    } catch (error) {
      console.error('Failed to search industries:', error);
    } finally {
      setLoadingIndustries(false);
    }
  };

  // Handle industry input change with debounce
  const handleIndustryInputChange = (value) => {
    setIndustrySearch(value);
    // Don't set editedCard.industry until user selects from dropdown
    // This allows the dropdown to show while typing
    searchIndustries(value);
  };

  const selectIndustry = (industry) => {
    setEditedCard({ ...editedCard, industry });
    setIndustrySearch(industry);
    setIndustryResults([]);
  };
  
  const clearIndustry = () => {
    setEditedCard({ ...editedCard, industry: '' });
    setIndustrySearch('');
    setIndustryResults([]);
  };

  // Handle task completion toggle
  const handleCompleteTask = async (taskId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks/${taskId}/complete`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        fetchCardTasks(card.card_id);
      }
    } catch (error) {
      console.error('Failed to complete task:', error);
    }
  };

  // Handle adding a new task
  const handleAddTask = async () => {
    if (!newTaskData.title.trim()) {
      alert('Please enter a task title');
      return;
    }
    
    setSavingTask(true);
    try {
      // Use selected user or default to current user
      const assignedUserId = newTaskData.assigned_to_id || currentUser?.user_id;
      
      const response = await fetch(`${BACKEND_URL}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: newTaskData.title,
          task_type: newTaskData.task_type,
          priority: newTaskData.priority,
          description: newTaskData.description,
          due_date: newTaskData.due_date,
          due_time: newTaskData.due_time,
          linked_card_id: card.card_id,
          assigned_to_id: assignedUserId
        })
      });
      
      if (response.ok) {
        setNewTaskData({
          title: '',
          task_type: 'other',
          priority: 'medium',
          description: '',
          due_date: '',
          due_time: '',
          assigned_to_id: ''
        });
        setShowAddTaskModal(false);
        fetchCardTasks(card.card_id);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add task');
      }
    } catch (error) {
      console.error('Add task error:', error);
      alert('Failed to add task');
    } finally {
      setSavingTask(false);
    }
  };

  // Fetch synced emails for ALL contacts on the card
  // Only syncs if no emails exist in database (first time), otherwise just fetches stored emails
  const fetchSyncedEmails = async (contactEmails) => {
    if (!contactEmails || contactEmails.length === 0) {
      console.log('fetchSyncedEmails: No contact emails provided');
      return;
    }
    
    console.log('fetchSyncedEmails: Fetching for', contactEmails);
    setLoadingSyncedEmails(true);
    
    try {
      let allEmails = [];
      let needsSync = false;
      
      // First, try to fetch existing emails from database for each contact
      for (const contactEmail of contactEmails) {
        if (!contactEmail) continue;
        
        try {
          const response = await fetch(`${BACKEND_URL}/api/synced-emails/${encodeURIComponent(contactEmail)}`, { 
            credentials: 'include' 
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log(`Fetched stored emails for ${contactEmail}:`, data.count);
            if (data.emails && data.emails.length > 0) {
              allEmails = [...allEmails, ...data.emails];
            } else {
              // No emails stored for this contact - needs sync
              needsSync = true;
            }
          }
        } catch (e) {
          console.log(`Fetch failed for ${contactEmail}:`, e);
          needsSync = true;
        }
      }
      
      // Only trigger sync if we found no emails (first time viewing)
      if (needsSync && allEmails.length === 0) {
        console.log('No stored emails found - triggering sync...');
        
        for (const contactEmail of contactEmails) {
          if (!contactEmail) continue;
          
          try {
            const syncResponse = await fetch(`${BACKEND_URL}/api/synced-emails/sync/${encodeURIComponent(contactEmail)}?hours=168`, { 
              method: 'POST',
              credentials: 'include' 
            });
            
            if (syncResponse.ok) {
              const syncResult = await syncResponse.json();
              console.log(`Sync result for ${contactEmail}:`, syncResult);
            }
          } catch (e) {
            console.log(`Sync failed for ${contactEmail}:`, e);
          }
          
          // Fetch again after sync
          try {
            const response = await fetch(`${BACKEND_URL}/api/synced-emails/${encodeURIComponent(contactEmail)}`, { 
              credentials: 'include' 
            });
            
            if (response.ok) {
              const data = await response.json();
              if (data.emails && data.emails.length > 0) {
                allEmails = [...allEmails, ...data.emails];
              }
            }
          } catch (e) {
            // Ignore
          }
        }
      }
      
      // Remove duplicates by email_id
      const uniqueEmails = allEmails.filter((email, index, self) => 
        index === self.findIndex(e => e.email_id === email.email_id)
      );
      
      console.log('Total unique emails:', uniqueEmails.length);
      setSyncedEmails(uniqueEmails);
      
    } catch (error) {
      console.error('Failed to fetch synced emails:', error);
      setSyncedEmails([]);
    } finally {
      setLoadingSyncedEmails(false);
    }
  };

  const fetchCardProposals = async (cardId) => {
    setLoadingProposals(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/cards/${cardId}/proposals`, { credentials: 'include' });
      if (response.ok) {
        setCardProposals(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch proposals:', error);
    } finally {
      setLoadingProposals(false);
    }
  };

  const fetchProposalTemplates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/proposal-templates`, { credentials: 'include' });
      if (response.ok) {
        setProposalTemplates(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const createProposal = async (templateId) => {
    setCreatingProposal(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/proposals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          template_id: templateId,
          card_id: card.card_id
        })
      });
      
      if (response.ok) {
        const newProposal = await response.json();
        onClose();
        navigate(`/proposals/${newProposal.proposal_id}/edit`);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create proposal');
      }
    } catch (error) {
      console.error('Create proposal error:', error);
      alert('Failed to create proposal');
    } finally {
      setCreatingProposal(false);
    }
  };

  // Fetch proposals when tab changes to proposals
  useEffect(() => {
    if (activeTab === 'proposals' && card?.card_id) {
      fetchCardProposals(card.card_id);
      fetchProposalTemplates();
    }
  }, [activeTab, card?.card_id]);

  // Fetch emails when Notes tab is active
  useEffect(() => {
    // Collect ALL email addresses from the card
    const emailAddresses = new Set();
    
    // Primary contact email
    if (editedCard?.primary_contact?.email) {
      emailAddresses.add(editedCard.primary_contact.email.toLowerCase());
    }
    
    // Key people emails
    if (editedCard?.key_people && Array.isArray(editedCard.key_people)) {
      editedCard.key_people.forEach(person => {
        if (person?.email) {
          emailAddresses.add(person.email.toLowerCase());
        }
      });
    }
    
    // Other potential email fields
    if (editedCard?.contact_email) {
      emailAddresses.add(editedCard.contact_email.toLowerCase());
    }
    if (editedCard?.email) {
      emailAddresses.add(editedCard.email.toLowerCase());
    }
    
    const allEmails = Array.from(emailAddresses);
    console.log('Notes tab - All contact emails on card:', allEmails, 'activeTab:', activeTab);
    
    if (activeTab === 'notes' && allEmails.length > 0 && card?.card_id) {
      fetchSyncedEmails(allEmails);
    }
  }, [activeTab, card?.card_id]);

  // Effect to load preview when modal opens
  useEffect(() => {
    if (showSpeakerInviteModal && card) {
      loadSpeakerInvitePreview();
    }
  }, [showSpeakerInviteModal, card]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setSavingNote(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/cards/${card.card_id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newNote })
      });
      
      if (response.ok) {
        setNewNote('');
        fetchCardNotes(card.card_id);
      } else {
        alert('Failed to add note');
      }
    } catch (error) {
      console.error('Add note error:', error);
      alert('Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  if (!isOpen || !card || !editedCard) return null;

  // Helper to update speaker at index
  const updateSpeaker = (index, field, value) => {
    const speakers = [...(editedCard.speakers || [])];
    while (speakers.length <= index) {
      speakers.push({});
    }
    speakers[index] = { ...speakers[index], [field]: value };
    setEditedCard({ ...editedCard, speakers });
  };

  // Helper to get speaker field
  const getSpeaker = (index, field) => {
    const speakers = editedCard.speakers || [];
    return speakers[index]?.[field] || '';
  };

  // Helper to update key person by role
  const updateKeyPerson = (role, field, value) => {
    const keyPeople = [...(editedCard.key_people || [])];
    const existingIndex = keyPeople.findIndex(p => p.role === role);
    if (existingIndex >= 0) {
      keyPeople[existingIndex] = { ...keyPeople[existingIndex], [field]: value };
      setEditedCard({ ...editedCard, key_people: keyPeople });
    } else {
      // Only create new contact if value is not empty
      if (value && value.toString().trim()) {
        keyPeople.push({ role, [field]: value });
        setEditedCard({ ...editedCard, key_people: keyPeople });
      }
    }
  };

  // Helper to get key person field by role
  const getKeyPerson = (role, field) => {
    const person = (editedCard.key_people || []).find(p => p.role === role);
    return person?.[field] || '';
  };

  // Helper to add/remove additional contacts (role: "other")
  const addContact = () => {
    const keyPeople = [...(editedCard.key_people || [])];
    keyPeople.push({ role: 'other', first_name: '', last_name: '', title: '', email: '', phone: '' });
    setEditedCard({ ...editedCard, key_people: keyPeople });
  };

  const removeContact = (index) => {
    const otherContacts = (editedCard.key_people || []).filter(p => p.role === 'other');
    const filteredOthers = otherContacts.filter((_, i) => i !== index);
    const nonOthers = (editedCard.key_people || []).filter(p => p.role !== 'other');
    setEditedCard({ ...editedCard, key_people: [...nonOthers, ...filteredOthers] });
  };

  const updateOtherContact = (index, field, value) => {
    const keyPeople = [...(editedCard.key_people || [])];
    const otherIndexes = keyPeople.map((p, i) => p.role === 'other' ? i : -1).filter(i => i >= 0);
    if (otherIndexes[index] !== undefined) {
      keyPeople[otherIndexes[index]] = { ...keyPeople[otherIndexes[index]], [field]: value };
      setEditedCard({ ...editedCard, key_people: keyPeople });
    }
  };

  const getOtherContacts = () => {
    return (editedCard.key_people || []).filter(p => p.role === 'other');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Filter out empty contacts before saving
      const cleanedKeyPeople = (editedCard.key_people || []).filter(person => {
        const hasFirstName = person.first_name?.toString().trim();
        const hasLastName = person.last_name?.toString().trim();
        const hasName = person.name?.toString().trim();
        const hasEmail = person.email?.toString().trim();
        // Keep contact if it has a name OR email
        return hasFirstName || hasLastName || hasName || hasEmail;
      });

      const response = await fetch(`${BACKEND_URL}/api/cards/${card.card_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: editedCard.title,
          description: editedCard.description,
          relationship_status: editedCard.relationship_status,
          contract_date: editedCard.contract_date,
          churn_date: editedCard.churn_date,
          event_date: editedCard.event_date,
          customer_bio: editedCard.customer_bio,
          // Rationalized arrays
          speakers: editedCard.speakers,
          key_people: cleanedKeyPeople,
          color_pinned: editedCard.color_pinned,
          // KYC fields
          company_address: editedCard.company_address,
          city_town: editedCard.city_town,
          zip_code: editedCard.zip_code,
          country: editedCard.country,
          company_phone: editedCard.company_phone,
          website_url: editedCard.website_url,
          company_registration: editedCard.company_registration,
          industry: editedCard.industry,
          // Relationship owner (admin only)
          relationship_owner_id: editedCard.relationship_owner_id
        })
      });

      if (response.ok) {
        onUpdate();
        // Don't close - allow user to continue editing
      } else {
        alert('Failed to update card');
      }
    } catch (error) {
      console.error('Update error:', error);
      alert('Failed to update card');
    } finally {
      setSaving(false);
    }
  };

  const handleSpeakerPhotoUpload = async (speakerIndex, file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('photo', file);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/cards/${card.card_id}/speaker-photo/${speakerIndex}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        updateSpeaker(speakerIndex, 'photo_url', data.url);
        alert('Photo uploaded successfully!');
      } else {
        alert('Failed to upload photo');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload photo');
    }
  };

  const handleCustomerLogoUpload = async (file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('photo', file);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/cards/${card.card_id}/customer-logo`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setEditedCard({ ...editedCard, customer_photo_url: data.url, asset_collection_status: 'submitted' });
        alert('Logo uploaded successfully!');
      } else {
        alert('Failed to upload logo');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload logo');
    }
  };

  const generateSpeakerToken = async (speakerIndex) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cards/${card.card_id}/speaker-token/${speakerIndex}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        updateSpeaker(speakerIndex, 'upload_token', data.token);
        alert('Upload link generated successfully!');
      } else {
        alert('Failed to generate upload link');
      }
    } catch (error) {
      console.error('Token generation error:', error);
      alert('Failed to generate upload link');
    }
  };

  // Load speaker invite email preview
  const loadSpeakerInvitePreview = async () => {
    setSpeakerInviteLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/system-emails/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          template_key: 'speaker_confirmation',
          card_id: card.card_id,
          custom_variables: {
            event_name: editedCard.event_title || editedCard.title || '',
            event_date: editedCard.event_date ? new Date(editedCard.event_date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '',
            company_name: editedCard.title || ''
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSpeakerInvitePreview(data);
        
        // Pre-select speakers with emails
        const speakersWithEmail = [];
        const speakers = editedCard.speakers || [];
        speakers.forEach((speaker, idx) => {
          if (speaker.email) {
            speakersWithEmail.push(idx);
          }
        });
        setSelectedSpeakers(speakersWithEmail);
      }
    } catch (error) {
      console.error('Failed to load speaker invite preview:', error);
    } finally {
      setSpeakerInviteLoading(false);
    }
  };

  // Send speaker invites
  const sendSpeakerInvites = async () => {
    if (selectedSpeakers.length === 0) {
      alert('Please select at least one speaker');
      return;
    }
    
    setSpeakerInviteSending(true);
    const results = [];
    
    try {
      for (const speakerIdx of selectedSpeakers) {
        const speaker = (editedCard.speakers || [])[speakerIdx];
        if (!speaker || !speaker.email) continue;
        
        // Personalize the email for each speaker
        const personalizedSubject = speakerInvitePreview.subject.replace(/\{\{\s*recipient_name\s*\}\}/g, speaker.name || 'Speaker');
        const personalizedBody = speakerInvitePreview.body
          .replace(/\{\{\s*recipient_name\s*\}\}/g, speaker.name || 'Speaker')
          .replace(/\{\{\s*recipient_first_name\s*\}\}/g, (speaker.name || 'Speaker').split(' ')[0]);
        
        const response = await fetch(`${BACKEND_URL}/api/system-emails/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            to_email: speaker.email,
            to_name: speaker.name || 'Speaker',
            subject: personalizedSubject,
            body: personalizedBody,
            template_key: 'speaker_confirmation',
            card_id: card.card_id
          })
        });
        
        const result = await response.json();
        results.push({ speaker: speaker.name, email: speaker.email, success: result.success, error: result.error });
        
        // Update speaker with invite_sent_at
        if (result.success) {
          updateSpeaker(speakerIdx, 'invite_sent_at', new Date().toISOString());
        }
      }
      
      // Show results
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;
      
      if (failed === 0) {
        alert(`Successfully sent ${successful} speaker invite(s)!`);
        setShowSpeakerInviteModal(false);
      } else {
        const failedNames = results.filter(r => !r.success).map(r => `${r.speaker}: ${r.error}`).join('\n');
        alert(`Sent ${successful} invite(s). Failed: ${failed}\n\n${failedNames}`);
      }
    } catch (error) {
      console.error('Failed to send speaker invites:', error);
      alert('Failed to send speaker invites');
    } finally {
      setSpeakerInviteSending(false);
    }
  };

  // Fetch card history
  const fetchCardHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/cards/${card.card_id}/history`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCardHistory(data.history || []);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Handle retire card
  const handleRetire = async () => {
    if (!retireReason) {
      alert('Please select a reason for retirement');
      return;
    }
    setRetiring(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/cards/${card.card_id}/retire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: retireReason })
      });
      if (response.ok) {
        setShowRetireModal(false);
        onUpdate();
        onClose();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to retire card');
      }
    } catch (error) {
      console.error('Retire error:', error);
      alert('Failed to retire card');
    } finally {
      setRetiring(false);
    }
  };

  // Handle reassign card
  const handleReassign = async () => {
    if (!reassignUserId) {
      alert('Please select a user to reassign the card to');
      return;
    }
    setReassigning(true);
    try {
      const selectedUser = users.find(u => u.user_id === reassignUserId);
      const response = await fetch(`${BACKEND_URL}/api/cards/${card.card_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          relationship_owner_id: reassignUserId,
          relationship_owner_name: selectedUser?.name || 'Unknown'
        })
      });
      if (response.ok) {
        setShowReassignModal(false);
        setReassignUserId('');
        // Update local state
        setEditedCard({
          ...editedCard,
          relationship_owner_id: reassignUserId,
          relationship_owner_name: selectedUser?.name || 'Unknown'
        });
        onUpdate();
        alert(`Card reassigned to ${selectedUser?.name || 'selected user'}`);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to reassign card');
      }
    } catch (error) {
      console.error('Reassign error:', error);
      alert('Failed to reassign card');
    } finally {
      setReassigning(false);
    }
  };

  // Handle reinstate card
  const handleReinstate = async () => {
    setReinstating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/cards/${card.card_id}/reinstate`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        onUpdate();
        onClose();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to reinstate card');
      }
    } catch (error) {
      console.error('Reinstate error:', error);
      alert('Failed to reinstate card');
    } finally {
      setReinstating(false);
    }
  };

  // Check if this is a member_tracking card (either native or linked to media_production)
  const isMemberTracking = boardType === 'member_tracking' || card?.show_in_media_production === true;

  // Check if retirement is allowed for current column (only for native member_tracking cards, not linked view)
  const canRetire = boardType === 'member_tracking' && columnName && RETIREMENT_ALLOWED_COLUMNS.includes(columnName) && !card?.is_retired;

  // Get primary contact email for synced emails fetch
  const primaryContactEmail = editedCard?.primary_contact?.email || editedCard?.key_people?.[0]?.email;

  const tabs = [
    { id: 'details', label: 'Details', icon: FileText },
    ...(isMemberTracking ? [
      { id: 'contacts', label: 'Contacts', icon: Users },
      { id: 'notes', label: 'Notes', icon: MessageSquare },
      { id: 'proposals', label: 'Proposals', icon: FileSignature },
      { id: 'qsecdef-event', label: 'Webinar', icon: Calendar },
      { id: 'operations', label: 'Operations', icon: ClipboardList },
      { id: 'media', label: 'Media', icon: Film },
      { id: 'history', label: 'History', icon: History }
    ] : [])
  ];

  return (
    <div className={`fixed inset-0 ${isDark ? 'bg-black/80' : 'bg-black/50'} flex items-center justify-center z-50 p-4`} onClick={onClose}>
      <div 
        className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col`}
        onClick={(e) => e.stopPropagation()}
        data-testid="card-detail-modal"
      >
        {/* Header */}
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border-b p-6 flex items-center justify-between flex-shrink-0`}>
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Card Details
          </h2>
          <button
            onClick={onClose}
            data-testid="close-modal"
            className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'} transition-colors`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs Navigation */}
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border-b px-6 flex gap-1 overflow-x-auto flex-shrink-0`}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? `${isDark ? 'text-[#A2FF00] border-[#A2FF00]' : 'text-green-600 border-green-600'} border-b-2`
                    : `${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content - Scrollable */}
        <div className="p-6 space-y-6 overflow-y-auto flex-grow">
          
          {/* Details Tab */}
          {activeTab === 'details' && (
            <>
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Company Name *</label>
                  <input
                    type="text"
                    value={editedCard.title}
                    onChange={(e) => setEditedCard({ ...editedCard, title: e.target.value })}
                    data-testid="card-title-input"
                    className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] rounded-sm p-3`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Description</label>
                  <textarea
                    value={editedCard.description || ''}
                    onChange={(e) => setEditedCard({ ...editedCard, description: e.target.value })}
                    rows={4}
                    data-testid="card-description-input"
                    className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] rounded-sm p-3`}
                  />
                </div>

                {/* Industry Field - Available on all boards */}
                <div className="mt-4">
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Industry</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={editedCard.industry ? editedCard.industry : industrySearch}
                      onChange={(e) => {
                        // If there's a selected industry, clear it when user starts typing again
                        if (editedCard.industry) {
                          setEditedCard({ ...editedCard, industry: '' });
                        }
                        handleIndustryInputChange(e.target.value);
                      }}
                      placeholder="Start typing to search industries..."
                      className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3`}
                    />
                  </div>
                  {/* Industry Search Results */}
                  {industrySearch.length >= 2 && !editedCard.industry && (
                    <div className={`mt-2 ${isDark ? 'bg-[#111] border-white/20' : 'bg-white border-gray-200'} border rounded-sm max-h-48 overflow-y-auto`}>
                      {loadingIndustries ? (
                        <div className="px-3 py-3 text-center text-gray-400">Searching...</div>
                      ) : industryResults.length > 0 ? (
                        <div className="divide-y divide-white/10">
                          {industryResults.map((industry, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => selectIndustry(industry)}
                              className={`w-full px-3 py-2 text-left ${isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-gray-100 text-gray-900'} transition-colors`}
                            >
                              {industry}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="px-3 py-3 text-center text-gray-500">
                          No industries found matching &quot;{industrySearch}&quot;
                        </div>
                      )}
                    </div>
                  )}
                  {editedCard.industry && (
                    <button
                      type="button"
                      onClick={clearIndustry}
                      className={`text-xs ${isDark ? 'text-gray-500 hover:text-white' : 'text-gray-500 hover:text-gray-900'} mt-1`}
                    >
                      Clear industry
                    </button>
                  )}
                </div>
              </div>

              {/* Relationship Status & Ownership - Only for Member Tracking */}
              {isMemberTracking && (
                <div className="border-t border-white/10 pt-6">
                  <h3 className={`text-lg font-bold ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} mb-4 flex items-center gap-2`}>
                    <User className="w-5 h-5" />
                    Relationship Status & Ownership
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                        Relationship Status
                        {currentUser?.role !== 'admin' && (
                          <span className="text-xs text-gray-500 ml-2">(Admin only)</span>
                        )}
                      </label>
                      {currentUser?.role === 'admin' ? (
                        <select
                          value={editedCard.relationship_status || 'suspect'}
                          onChange={(e) => setEditedCard({ ...editedCard, relationship_status: e.target.value })}
                          data-testid="relationship-status-select"
                          className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3`}
                        >
                          <option value="suspect">Suspect</option>
                          <option value="prospect">Prospect</option>
                          <option value="customer">Customer</option>
                          <option value="paused">Paused</option>
                          <option value="renewing">Renewing</option>
                          <option value="churned">Churned</option>
                        </select>
                      ) : (
                        <div className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'} border rounded-sm p-3 capitalize`}>
                          {editedCard.relationship_status || 'suspect'}
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {editedCard.relationship_status === 'suspect' && 'New lead - not yet qualified'}
                        {editedCard.relationship_status === 'prospect' && 'Qualified or proposal created'}
                        {editedCard.relationship_status === 'customer' && 'Contract signed - active customer'}
                        {editedCard.relationship_status === 'paused' && 'Contract paused - no future revenue'}
                        {editedCard.relationship_status === 'renewing' && '2 months before contract anniversary'}
                        {editedCard.relationship_status === 'churned' && 'Did not renew - card will be retired'}
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                        Relationship Owner
                      </label>
                      <div className="flex items-center gap-3">
                        <div className={`flex-1 ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'} border rounded-sm p-3`}>
                          {editedCard.relationship_owner_name || 'Unassigned'}
                        </div>
                        <select
                          value={editedCard.relationship_owner_id || ''}
                          onChange={(e) => {
                            const selectedUser = users.find(u => u.user_id === e.target.value);
                            setEditedCard({
                              ...editedCard,
                              relationship_owner_id: e.target.value,
                              relationship_owner_name: selectedUser?.name || 'Unknown'
                            });
                          }}
                          className={`${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3`}
                        >
                          <option value="">Select Owner</option>
                          {users.map(user => (
                            <option key={user.user_id} value={user.user_id}>{user.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {editedCard.relationship_status === 'churned' && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
                      <p className="text-red-400 text-sm flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Setting status to Churned will automatically retire this card.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* KYC Section - Only for Member Tracking */}
              {isMemberTracking && (
                <div className="border-t border-white/10 pt-6">
                  <h3 className={`text-lg font-bold ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} mb-4 flex items-center gap-2`}>
                    <Building className="w-5 h-5" />
                    KYC
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Company Address</label>
                      <input
                        type="text"
                        value={editedCard.company_address || ''}
                        onChange={(e) => setEditedCard({ ...editedCard, company_address: e.target.value })}
                        placeholder="123 Business Street, Suite 100"
                        className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3`}
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>City / Town</label>
                      <input
                        type="text"
                        value={editedCard.city_town || ''}
                        onChange={(e) => setEditedCard({ ...editedCard, city_town: e.target.value })}
                        placeholder="London"
                        className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3`}
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Zip / Postal Code</label>
                      <input
                        type="text"
                        value={editedCard.zip_code || ''}
                        onChange={(e) => setEditedCard({ ...editedCard, zip_code: e.target.value })}
                        placeholder="SW1A 1AA"
                        className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3`}
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Country</label>
                      <input
                        type="text"
                        value={editedCard.country || ''}
                        onChange={(e) => setEditedCard({ ...editedCard, country: e.target.value })}
                        placeholder="United Kingdom"
                        className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3`}
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Company Phone</label>
                      <input
                        type="tel"
                        value={editedCard.company_phone || ''}
                        onChange={(e) => setEditedCard({ ...editedCard, company_phone: e.target.value })}
                        placeholder="+44 20 1234 5678"
                        className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3`}
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Website URL</label>
                      <input
                        type="url"
                        value={editedCard.website_url || ''}
                        onChange={(e) => setEditedCard({ ...editedCard, website_url: e.target.value })}
                        placeholder="https://www.company.com"
                        className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3`}
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Company Registration</label>
                      <input
                        type="text"
                        value={editedCard.company_registration || ''}
                        onChange={(e) => setEditedCard({ ...editedCard, company_registration: e.target.value })}
                        placeholder="12345678"
                        className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3`}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Card Information */}
              <div className="border-t border-white/10 pt-6">
                <h3 className="text-lg font-bold text-gray-400 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Card Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Created:</span>
                    <span className="text-gray-300 ml-2">
                      {new Date(editedCard.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Last Updated:</span>
                    <span className="text-gray-300 ml-2">
                      {new Date(editedCard.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Last Moved:</span>
                    <span className="text-gray-300 ml-2">
                      {new Date(editedCard.last_moved_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Card ID:</span>
                    <span className="text-gray-300 ml-2 font-mono text-xs">{editedCard.card_id}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* QSECDEF Event Tab - Combined Events & Media + Event Speakers */}
          {activeTab === 'qsecdef-event' && isMemberTracking && (
            <div className="space-y-6">
              {/* Event Details Section */}
              <div>
                <h3 className={`text-lg font-bold ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} mb-4 flex items-center gap-2`}>
                  <Calendar className="w-5 h-5" />
                  Event Details
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>QSECDEF Event Date</label>
                    <div className="relative">
                      <DatePicker
                        selected={editedCard.event_date ? new Date(editedCard.event_date) : null}
                        onChange={(date) => setEditedCard({ ...editedCard, event_date: date ? date.toISOString() : null })}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select event date"
                        showPopperArrow={false}
                        calendarClassName="qsecdef-calendar"
                        className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3 cursor-pointer`}
                        wrapperClassName="w-full"
                        popperClassName="qsecdef-calendar-popper"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        isClearable
                      />
                      <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" />
                    </div>
                    {editedCard.asset_deadline && (
                      <p className="text-xs text-gray-500 mt-1">
                        Asset deadline: {new Date(editedCard.asset_deadline).toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  {editedCard.event_date && (
                    <>
                      <div>
                        <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Public Company Description</label>
                        <p className="text-xs text-gray-500 mb-2">Description used in media</p>
                        <textarea
                          value={editedCard.customer_bio || ''}
                          onChange={(e) => setEditedCard({ ...editedCard, customer_bio: e.target.value })}
                          rows={3}
                          maxLength={2000}
                          placeholder="Company description for event..."
                          className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3`}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {editedCard.customer_bio?.length || 0}/2000 characters
                        </p>
                      </div>

                      <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-4`}>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-400">Company Logo</label>
                          <span className={`text-xs px-2 py-1 rounded ${
                            editedCard.asset_collection_status === 'submitted' 
                              ? 'bg-green-500/20 text-green-400'
                              : editedCard.asset_collection_status === 'overdue'
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {editedCard.asset_collection_status || 'pending'}
                          </span>
                        </div>
                        
                        {/* Admin Logo Upload Override */}
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-500 mb-1">Upload/Override Logo</label>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(e) => handleCustomerLogoUpload(e.target.files[0])}
                            className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border p-1.5 rounded-sm text-xs file:mr-2 file:py-1 file:px-2 file:rounded-sm file:border-0 ${isDark ? 'file:bg-[#A2FF00]' : 'file:bg-emerald-500'} file:text-black file:font-bold file:text-xs`}
                          />
                          <p className="text-xs text-gray-500 mt-1">Add company logo in JPEG, WebP or PNG</p>
                        </div>
                        
                        {editedCard.customer_photo_url && (
                          <div className="mt-3">
                            <img 
                              src={`${BACKEND_URL}${editedCard.customer_photo_url}`} 
                              alt="Customer Logo" 
                              className={`w-24 h-24 object-cover rounded-sm border ${isDark ? 'border-[#A2FF00]/20' : 'border-emerald-200'}`}
                            />
                            <button
                              onClick={() => downloadFileWithAuth(
                                `${BACKEND_URL}${editedCard.customer_photo_url}`,
                                editedCard.customer_photo_url.split('/').pop()
                              )}
                              className={`text-xs ${isDark ? 'text-[#A2FF00] hover:text-[#8EFF00]' : 'text-emerald-600 hover:text-emerald-700'} mt-2 inline-flex items-center gap-1`}
                            >
                              <Download className="w-3 h-3" />
                              Download Logo
                            </button>
                          </div>
                        )}
                        {editedCard.asset_upload_token && (
                          <div className="mt-3">
                            <p className="text-xs text-gray-500 mb-1">Customer Upload Link:</p>
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={`${window.location.origin}/upload/${editedCard.asset_upload_token}`}
                                readOnly
                                className={`flex-1 ${isDark ? 'bg-[#0A0A0A] border-white/10 text-gray-400' : 'bg-gray-100 border-gray-300 text-gray-600'} border rounded-sm text-xs p-2`}
                              />
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(`${window.location.origin}/upload/${editedCard.asset_upload_token}`);
                                  alert('Link copied to clipboard!');
                                }}
                                className={`text-xs ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-emerald-500 hover:bg-emerald-600'} text-black px-3 py-2 rounded-sm transition-colors`}
                              >
                                Copy
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Event Speakers Section */}
              <div className="border-t border-white/10 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-lg font-bold ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} flex items-center gap-2`}>
                    <User className="w-5 h-5" />
                    Event Speakers
                  </h3>
                  <button
                    onClick={() => setShowSpeakerInviteModal(true)}
                    disabled={!getSpeaker(0, 'email') && !getSpeaker(1, 'email')}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium transition-colors ${
                      (getSpeaker(0, 'email') || getSpeaker(1, 'email'))
                        ? 'bg-[#A2FF00] text-black hover:bg-[#8FE500]'
                        : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    }`}
                    title={(!getSpeaker(0, 'email') && !getSpeaker(1, 'email')) ? 'Add speaker emails first' : 'Send confirmation emails to speakers'}
                  >
                    <Mail className="w-4 h-4" />
                    Send Speaker Invites
                  </button>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Speaker 1 (index 0) */}
                  <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-4`}>
                    <h4 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>Speaker 1</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                        <input
                          type="text"
                          value={getSpeaker(0, 'name')}
                          onChange={(e) => updateSpeaker(0, 'name', e.target.value)}
                          className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                          placeholder="Dr. Jane Smith"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
                        <input
                          type="text"
                          value={getSpeaker(0, 'title')}
                          onChange={(e) => updateSpeaker(0, 'title', e.target.value)}
                          className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                          placeholder="Chief Security Officer"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> Email
                        </label>
                        <input
                          type="email"
                          value={getSpeaker(0, 'email')}
                          onChange={(e) => updateSpeaker(0, 'email', e.target.value)}
                          className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                          placeholder="speaker@company.com"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                          <Linkedin className="w-3 h-3" /> LinkedIn URL
                        </label>
                        <input
                          type="url"
                          value={getSpeaker(0, 'linkedin_url')}
                          onChange={(e) => updateSpeaker(0, 'linkedin_url', e.target.value)}
                          className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                          placeholder="https://linkedin.com/in/username"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Photo</label>
                        <div className="space-y-2">
                          {getSpeaker(0, 'photo_url') && (
                            <div className="flex items-center gap-3">
                              <img 
                                src={`${BACKEND_URL}${getSpeaker(0, 'photo_url')}`} 
                                alt="Speaker 1" 
                                className={`w-16 h-16 object-cover rounded-sm border ${isDark ? 'border-[#A2FF00]/20' : 'border-emerald-200'}`}
                              />
                              <button
                                onClick={() => downloadFileWithAuth(
                                  `${BACKEND_URL}${getSpeaker(0, 'photo_url')}`,
                                  getSpeaker(0, 'photo_url').split('/').pop()
                                )}
                                className={`text-xs ${isDark ? 'text-[#A2FF00] hover:text-[#8EFF00]' : 'text-emerald-600 hover:text-emerald-700'} inline-flex items-center gap-1`}
                              >
                                <Download className="w-3 h-3" />
                                Download
                              </button>
                            </div>
                          )}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(e) => handleSpeakerPhotoUpload(0, e.target.files[0])}
                            className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border p-1.5 rounded-sm text-xs file:mr-2 file:py-1 file:px-2 file:rounded-sm file:border-0 ${isDark ? 'file:bg-[#A2FF00]' : 'file:bg-emerald-500'} file:text-black file:font-bold file:text-xs`}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Bio</label>
                        <textarea
                          value={getSpeaker(0, 'bio')}
                          onChange={(e) => updateSpeaker(0, 'bio', e.target.value)}
                          rows={2}
                          className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                          placeholder="Brief bio..."
                        />
                      </div>

                      {/* Speaker 1 Upload Link */}
                      <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-3 mt-2`}>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
                            <Link className="w-3 h-3" /> Speaker Upload Link
                          </label>
                          <button
                            onClick={() => generateSpeakerToken(0)}
                            className={`text-xs ${isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00] hover:bg-[#A2FF00]/30' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'} px-2 py-1 rounded-sm transition-colors flex items-center gap-1`}
                          >
                            <RefreshCw className="w-3 h-3" />
                            {getSpeaker(0, 'upload_token') ? 'Regenerate' : 'Generate'}
                          </button>
                        </div>
                        {getSpeaker(0, 'upload_token') ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={`${window.location.origin}/speaker-upload/${getSpeaker(0, 'upload_token')}`}
                              readOnly
                              className={`flex-1 ${isDark ? 'bg-[#111] border-white/10 text-gray-400' : 'bg-gray-100 border-gray-300 text-gray-600'} border rounded-sm text-xs p-1.5`}
                            />
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(`${window.location.origin}/speaker-upload/${getSpeaker(0, 'upload_token')}`);
                                alert('Link copied!');
                              }}
                              className={`text-xs ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-emerald-500 hover:bg-emerald-600'} text-black px-2 py-1.5 rounded-sm transition-colors`}
                            >
                              Copy
                            </button>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500">Click Generate to create a unique upload link for this speaker</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Speaker 2 (index 1) */}
                  <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-4`}>
                    <h4 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>Speaker 2</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                        <input
                          type="text"
                          value={getSpeaker(1, 'name')}
                          onChange={(e) => updateSpeaker(1, 'name', e.target.value)}
                          className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                          placeholder="Dr. John Doe"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
                        <input
                          type="text"
                          value={getSpeaker(1, 'title')}
                          onChange={(e) => updateSpeaker(1, 'title', e.target.value)}
                          className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                          placeholder="VP of Engineering"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> Email
                        </label>
                        <input
                          type="email"
                          value={getSpeaker(1, 'email')}
                          onChange={(e) => updateSpeaker(1, 'email', e.target.value)}
                          className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                          placeholder="speaker@company.com"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                          <Linkedin className="w-3 h-3" /> LinkedIn URL
                        </label>
                        <input
                          type="url"
                          value={getSpeaker(1, 'linkedin_url')}
                          onChange={(e) => updateSpeaker(1, 'linkedin_url', e.target.value)}
                          className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                          placeholder="https://linkedin.com/in/username"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Photo</label>
                        <div className="space-y-2">
                          {getSpeaker(1, 'photo_url') && (
                            <div className="flex items-center gap-3">
                              <img 
                                src={`${BACKEND_URL}${getSpeaker(1, 'photo_url')}`} 
                                alt="Speaker 2" 
                                className={`w-16 h-16 object-cover rounded-sm border ${isDark ? 'border-[#A2FF00]/20' : 'border-emerald-200'}`}
                              />
                              <button
                                onClick={() => downloadFileWithAuth(
                                  `${BACKEND_URL}${getSpeaker(1, 'photo_url')}`,
                                  getSpeaker(1, 'photo_url').split('/').pop()
                                )}
                                className={`text-xs ${isDark ? 'text-[#A2FF00] hover:text-[#8EFF00]' : 'text-emerald-600 hover:text-emerald-700'} inline-flex items-center gap-1`}
                              >
                                <Download className="w-3 h-3" />
                                Download
                              </button>
                            </div>
                          )}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(e) => handleSpeakerPhotoUpload(1, e.target.files[0])}
                            className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border p-1.5 rounded-sm text-xs file:mr-2 file:py-1 file:px-2 file:rounded-sm file:border-0 ${isDark ? 'file:bg-[#A2FF00]' : 'file:bg-emerald-500'} file:text-black file:font-bold file:text-xs`}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Bio</label>
                        <textarea
                          value={getSpeaker(1, 'bio')}
                          onChange={(e) => updateSpeaker(1, 'bio', e.target.value)}
                          rows={2}
                          className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                          placeholder="Brief bio..."
                        />
                      </div>

                      {/* Speaker 2 Upload Link */}
                      <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-3 mt-2`}>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
                            <Link className="w-3 h-3" /> Speaker Upload Link
                          </label>
                          <button
                            onClick={() => generateSpeakerToken(1)}
                            className={`text-xs ${isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00] hover:bg-[#A2FF00]/30' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'} px-2 py-1 rounded-sm transition-colors flex items-center gap-1`}
                          >
                            <RefreshCw className="w-3 h-3" />
                            {getSpeaker(1, 'upload_token') ? 'Regenerate' : 'Generate'}
                          </button>
                        </div>
                        {getSpeaker(1, 'upload_token') ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={`${window.location.origin}/speaker-upload/${getSpeaker(1, 'upload_token')}`}
                              readOnly
                              className={`flex-1 ${isDark ? 'bg-[#111] border-white/10 text-gray-400' : 'bg-gray-100 border-gray-300 text-gray-600'} border rounded-sm text-xs p-1.5`}
                            />
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(`${window.location.origin}/speaker-upload/${getSpeaker(1, 'upload_token')}`);
                                alert('Link copied!');
                              }}
                              className={`text-xs ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-emerald-500 hover:bg-emerald-600'} text-black px-2 py-1.5 rounded-sm transition-colors`}
                            >
                              Copy
                            </button>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500">Click Generate to create a unique upload link for this speaker</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Key People Tab */}
          {activeTab === 'contacts' && isMemberTracking && (
            <div className="space-y-6">
              <h3 className={`text-lg font-bold ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} mb-4 flex items-center gap-2`}>
                <Users className="w-5 h-5" />
                Key People
              </h3>

              {/* Main Contact - Featured */}
              <div className={`bg-gradient-to-r ${isDark ? 'from-[#A2FF00]/10' : 'from-emerald-100'} to-transparent border-2 ${isDark ? 'border-[#A2FF00]/50' : 'border-emerald-500/50'} rounded-sm p-4`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} flex items-center gap-2`}>
                    <User className="w-4 h-4" />
                    Main Contact
                  </h4>
                  {/* LinkedIn Icon for Main Contact */}
                  <button
                    onClick={() => {
                      const url = getKeyPerson('main_contact', 'linkedin_url');
                      if (url) {
                        window.open(url, '_blank', 'noopener,noreferrer');
                      } else {
                        // Focus on the LinkedIn input field
                        const input = document.getElementById('main-contact-linkedin');
                        if (input) input.focus();
                      }
                    }}
                    className={`p-1.5 rounded transition-colors ${
                      getKeyPerson('main_contact', 'linkedin_url')
                        ? 'bg-[#0077B5] text-white hover:bg-[#005885]' 
                        : 'bg-white/10 text-gray-400 hover:text-white hover:bg-white/20'
                    }`}
                    title={getKeyPerson('main_contact', 'linkedin_url') ? 'Open LinkedIn Profile' : 'Click to add LinkedIn URL'}
                  >
                    <Linkedin className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">First Name</label>
                    <input
                      type="text"
                      value={getKeyPerson('main_contact', 'first_name')}
                      onChange={(e) => updateKeyPerson('main_contact', 'first_name', e.target.value)}
                      className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={getKeyPerson('main_contact', 'last_name')}
                      onChange={(e) => updateKeyPerson('main_contact', 'last_name', e.target.value)}
                      className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                      placeholder="Last name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
                    <input
                      type="text"
                      value={getKeyPerson('main_contact', 'title')}
                      onChange={(e) => updateKeyPerson('main_contact', 'title', e.target.value)}
                      className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                      placeholder="Job title"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                    <input
                      type="email"
                      value={getKeyPerson('main_contact', 'email')}
                      onChange={(e) => updateKeyPerson('main_contact', 'email', e.target.value)}
                      className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                      placeholder="email@company.com"
                    />
                  </div>
                </div>
                {/* Phone Number field for Main Contact */}
                <div className="grid md:grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      value={getKeyPerson('main_contact', 'phone')}
                      onChange={(e) => updateKeyPerson('main_contact', 'phone', e.target.value)}
                      className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                </div>
                {/* LinkedIn URL field for Main Contact */}
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                    <Linkedin className="w-3 h-3" /> LinkedIn URL
                  </label>
                  <input
                    id="main-contact-linkedin"
                    type="url"
                    value={getKeyPerson('main_contact', 'linkedin_url')}
                    onChange={(e) => updateKeyPerson('main_contact', 'linkedin_url', e.target.value)}
                    className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>
              </div>

              {/* Standard Contacts Grid */}
              <div className="grid md:grid-cols-2 gap-4">
                <ContactCard
                  label="Billing Contact"
                  firstNameValue={getKeyPerson('billing', 'first_name')}
                  lastNameValue={getKeyPerson('billing', 'last_name')}
                  titleValue={getKeyPerson('billing', 'title')}
                  emailValue={getKeyPerson('billing', 'email')}
                  phoneValue={getKeyPerson('billing', 'phone')}
                  linkedinValue={getKeyPerson('billing', 'linkedin_url')}
                  onFirstNameChange={(v) => updateKeyPerson('billing', 'first_name', v)}
                  onLastNameChange={(v) => updateKeyPerson('billing', 'last_name', v)}
                  onTitleChange={(v) => updateKeyPerson('billing', 'title', v)}
                  onEmailChange={(v) => updateKeyPerson('billing', 'email', v)}
                  onPhoneChange={(v) => updateKeyPerson('billing', 'phone', v)}
                  onLinkedinChange={(v) => updateKeyPerson('billing', 'linkedin_url', v)}
                  isDark={isDark}
                />
                <ContactCard
                  label="CMO"
                  firstNameValue={getKeyPerson('cmo', 'first_name')}
                  lastNameValue={getKeyPerson('cmo', 'last_name')}
                  titleValue={getKeyPerson('cmo', 'title')}
                  emailValue={getKeyPerson('cmo', 'email')}
                  phoneValue={getKeyPerson('cmo', 'phone')}
                  linkedinValue={getKeyPerson('cmo', 'linkedin_url')}
                  onFirstNameChange={(v) => updateKeyPerson('cmo', 'first_name', v)}
                  onLastNameChange={(v) => updateKeyPerson('cmo', 'last_name', v)}
                  onTitleChange={(v) => updateKeyPerson('cmo', 'title', v)}
                  onEmailChange={(v) => updateKeyPerson('cmo', 'email', v)}
                  onPhoneChange={(v) => updateKeyPerson('cmo', 'phone', v)}
                  onLinkedinChange={(v) => updateKeyPerson('cmo', 'linkedin_url', v)}
                  isDark={isDark}
                />
                <ContactCard
                  label="CRO"
                  firstNameValue={getKeyPerson('cro', 'first_name')}
                  lastNameValue={getKeyPerson('cro', 'last_name')}
                  titleValue={getKeyPerson('cro', 'title')}
                  emailValue={getKeyPerson('cro', 'email')}
                  phoneValue={getKeyPerson('cro', 'phone')}
                  linkedinValue={getKeyPerson('cro', 'linkedin_url')}
                  onFirstNameChange={(v) => updateKeyPerson('cro', 'first_name', v)}
                  onLastNameChange={(v) => updateKeyPerson('cro', 'last_name', v)}
                  onTitleChange={(v) => updateKeyPerson('cro', 'title', v)}
                  onEmailChange={(v) => updateKeyPerson('cro', 'email', v)}
                  onPhoneChange={(v) => updateKeyPerson('cro', 'phone', v)}
                  onLinkedinChange={(v) => updateKeyPerson('cro', 'linkedin_url', v)}
                  isDark={isDark}
                />
                <ContactCard
                  label="CEO"
                  firstNameValue={getKeyPerson('ceo', 'first_name')}
                  lastNameValue={getKeyPerson('ceo', 'last_name')}
                  titleValue={getKeyPerson('ceo', 'title')}
                  emailValue={getKeyPerson('ceo', 'email')}
                  phoneValue={getKeyPerson('ceo', 'phone')}
                  linkedinValue={getKeyPerson('ceo', 'linkedin_url')}
                  onFirstNameChange={(v) => updateKeyPerson('ceo', 'first_name', v)}
                  onLastNameChange={(v) => updateKeyPerson('ceo', 'last_name', v)}
                  onTitleChange={(v) => updateKeyPerson('ceo', 'title', v)}
                  onEmailChange={(v) => updateKeyPerson('ceo', 'email', v)}
                  onPhoneChange={(v) => updateKeyPerson('ceo', 'phone', v)}
                  onLinkedinChange={(v) => updateKeyPerson('ceo', 'linkedin_url', v)}
                  isDark={isDark}
                />
              </div>

              {/* Additional Contacts Section */}
              <div className="border-t border-white/10 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Additional Contacts</h4>
                  <button
                    onClick={addContact}
                    className={`flex items-center gap-2 px-3 py-1.5 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-emerald-500 hover:bg-emerald-600'} text-black text-sm font-bold rounded-sm transition-colors`}
                  >
                    <Plus className="w-4 h-4" />
                    Add Contact
                  </button>
                </div>

                {getOtherContacts().length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">
                    No additional contacts added yet. Click Add Contact to add more people.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {getOtherContacts().map((contact, index) => (
                      <div key={index} className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-4 relative`}>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-xs font-medium text-gray-500">Contact {index + 1}</h4>
                          <div className="flex items-center gap-2">
                            {/* LinkedIn Icon for Additional Contacts */}
                            <button
                              onClick={() => {
                                if (contact.linkedin_url) {
                                  window.open(contact.linkedin_url, '_blank', 'noopener,noreferrer');
                                } else {
                                  const input = document.getElementById(`other-contact-linkedin-${index}`);
                                  if (input) input.focus();
                                }
                              }}
                              className={`p-1.5 rounded transition-colors ${
                                contact.linkedin_url
                                  ? 'bg-[#0077B5] text-white hover:bg-[#005885]' 
                                  : 'bg-white/10 text-gray-400 hover:text-white hover:bg-white/20'
                              }`}
                              title={contact.linkedin_url ? 'Open LinkedIn Profile' : 'Click to add LinkedIn URL'}
                            >
                              <Linkedin className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => removeContact(index)}
                              className="text-red-400 hover:text-red-300 transition-colors"
                              title="Delete contact"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="grid md:grid-cols-5 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">First Name</label>
                            <input
                              type="text"
                              value={contact.first_name || ''}
                              onChange={(e) => updateOtherContact(index, 'first_name', e.target.value)}
                              className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                              placeholder="First name"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Last Name</label>
                            <input
                              type="text"
                              value={contact.last_name || ''}
                              onChange={(e) => updateOtherContact(index, 'last_name', e.target.value)}
                              className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                              placeholder="Last name"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
                            <input
                              type="text"
                              value={contact.title || ''}
                              onChange={(e) => updateOtherContact(index, 'title', e.target.value)}
                              className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                              placeholder="Job title"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                            <input
                              type="email"
                              value={contact.email || ''}
                              onChange={(e) => updateOtherContact(index, 'email', e.target.value)}
                              className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                              placeholder="email@company.com"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
                            <input
                              type="tel"
                              value={contact.phone || ''}
                              onChange={(e) => updateOtherContact(index, 'phone', e.target.value)}
                              className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                              placeholder="07123 456789"
                            />
                          </div>
                        </div>
                        {/* LinkedIn URL field for Additional Contacts */}
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                            <Linkedin className="w-3 h-3" /> LinkedIn URL
                          </label>
                          <input
                            id={`other-contact-linkedin-${index}`}
                            type="url"
                            value={contact.linkedin_url || ''}
                            onChange={(e) => updateOtherContact(index, 'linkedin_url', e.target.value)}
                            className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-2 text-sm`}
                            placeholder="https://linkedin.com/in/username"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && isMemberTracking && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Notes & Tasks</h3>
                <button
                  onClick={() => setShowAddTaskModal(true)}
                  className={`flex items-center gap-2 px-3 py-1.5 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-emerald-500 hover:bg-emerald-600'} text-black font-bold rounded-sm transition-colors text-sm`}
                >
                  <Plus className="w-4 h-4" />
                  Add Task
                </button>
              </div>

              {/* Tasks Section */}
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-4`}>
                <h4 className={`text-sm font-bold ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-3 flex items-center gap-2`}>
                  <CheckSquare className="w-4 h-4" />
                  Tasks ({cardTasks.filter(t => t.status === 'pending').length} pending)
                </h4>
                
                {loadingTasks ? (
                  <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Loading tasks...</p>
                ) : cardTasks.length === 0 ? (
                  <div className="text-center py-4">
                    <CheckSquare className={`w-8 h-8 ${isDark ? 'text-gray-600' : 'text-gray-300'} mx-auto mb-2`} />
                    <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      No tasks for this company yet.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {cardTasks.map(task => {
                      const today = new Date().toISOString().split('T')[0];
                      const isOverdue = task.status === 'pending' && task.due_date && task.due_date < today;
                      const isDueToday = task.due_date === today;
                      
                      return (
                        <div
                          key={task.task_id}
                          className={`${isDark ? 'bg-[#0A0A0A]' : 'bg-white'} border ${
                            task.status === 'completed' 
                              ? isDark ? 'border-white/10' : 'border-gray-200'
                              : isOverdue 
                                ? 'border-red-500/50'
                                : isDueToday
                                  ? 'border-green-500/50'
                                  : isDark ? 'border-white/20' : 'border-gray-200'
                          } rounded-sm p-3`}
                        >
                          <div className="flex items-start gap-3">
                            <button
                              onClick={() => task.status !== 'completed' && handleCompleteTask(task.task_id)}
                              className={`mt-0.5 flex-shrink-0 ${
                                task.status === 'completed'
                                  ? isDark ? 'text-[#A2FF00]' : 'text-emerald-600'
                                  : isDark ? 'text-gray-500 hover:text-[#A2FF00]' : 'text-gray-400 hover:text-emerald-500'
                              }`}
                              disabled={task.status === 'completed'}
                            >
                              {task.status === 'completed' ? (
                                <CheckSquare className="w-5 h-5" />
                              ) : (
                                <Square className="w-5 h-5" />
                              )}
                            </button>
                            
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium ${task.status === 'completed' ? 'line-through opacity-60' : ''} ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {task.title}
                              </p>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                {task.due_date && (
                                  <span className={`text-xs flex items-center gap-1 ${
                                    isOverdue ? 'text-red-400' : isDueToday ? 'text-green-400' : isDark ? 'text-gray-500' : 'text-gray-400'
                                  }`}>
                                    <Clock className="w-3 h-3" />
                                    {isOverdue ? `${task.days_overdue}d overdue` : task.due_date}
                                  </span>
                                )}
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  task.priority === 'high' 
                                    ? 'bg-red-500/20 text-red-400' 
                                    : task.priority === 'low'
                                      ? 'bg-blue-500/20 text-blue-400'
                                      : 'bg-yellow-500/20 text-yellow-400'
                                }`}>
                                  {task.priority}
                                </span>
                                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {task.assigned_to_name}
                                </span>
                              </div>
                            </div>
                            
                            <button
                              onClick={() => navigate(`/tasks`)}
                              className={`text-xs ${isDark ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                              View →
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Add New Note */}
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-4`}>
                <h4 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} mb-3`}>Add a Note</h4>
                <div className="space-y-3">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Type your note here..."
                    className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-white border-gray-200 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3 text-sm min-h-[80px] resize-y`}
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={handleAddNote}
                      disabled={savingNote || !newNote.trim()}
                      className={`${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-emerald-500 hover:bg-emerald-600'} text-black px-4 py-2 font-bold rounded-sm transition-colors disabled:opacity-50 flex items-center gap-2`}
                    >
                      <Plus className="w-4 h-4" />
                      {savingNote ? 'Adding...' : 'Add Note'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Notes & Synced Emails Timeline */}
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-4`}>
                <h4 className={`text-sm font-bold ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-3`}>Activity Timeline</h4>
                
                <div className="space-y-3">
                  {(() => {
                    // Build combined list of notes and synced emails
                    const items = [];
                    
                    // Add notes
                    cardNotes.forEach(note => {
                      items.push({
                        type: 'note',
                        id: note.note_id,
                        date: new Date(note.created_at || 0),
                        data: note
                      });
                    });
                    
                    // Add synced emails from batch job
                    syncedEmails.forEach(email => {
                      items.push({
                        type: 'email',
                        id: email.email_id,
                        date: new Date(email.date || 0),
                        data: email
                      });
                    });
                    
                    // Sort by date descending
                    items.sort((a, b) => b.date - a.date);
                    
                    if (items.length === 0) {
                      return (
                        <div className="text-center py-6">
                          <MessageSquare className={`w-10 h-10 ${isDark ? 'text-gray-600' : 'text-gray-300'} mx-auto mb-2`} />
                          <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            No notes or emails yet. Add the first note above.
                          </p>
                        </div>
                      );
                    }
                    
                    return items.map((item) => {
                      if (item.type === 'note') {
                        const note = item.data;
                        return (
                          <div 
                            key={`note-${note.note_id}`} 
                            className={`${isDark ? 'bg-[#0A0A0A] border-[#A2FF00]/30' : 'bg-green-50 border-green-200'} border-l-4 rounded-sm p-4`}
                          >
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <div className="flex items-center gap-2">
                                <div className={`w-6 h-6 rounded-full ${isDark ? 'bg-[#A2FF00]/20' : 'bg-emerald-100'} flex items-center justify-center`}>
                                  <MessageSquare className={`w-3 h-3 ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'}`} />
                                </div>
                                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{note.created_by_name || 'Unknown User'}</span>
                                <span className={`text-xs px-2 py-0.5 ${isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00]' : 'bg-emerald-100 text-emerald-700'} rounded`}>Note</span>
                              </div>
                              <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                {note.created_at ? new Date(note.created_at).toLocaleString() : 'Unknown date'}
                              </span>
                            </div>
                            <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'} text-sm whitespace-pre-wrap ml-8`}>{note.content}</p>
                          </div>
                        );
                      } else {
                        const email = item.data;
                        const isIncoming = email.direction === 'received';
                        
                        return (
                          <EmailNoteCard 
                            key={`email-${email.email_id}`}
                            email={email}
                            isIncoming={isIncoming}
                            isDark={isDark}
                          />
                        );
                      }
                    });
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Add Task Modal */}
          {showAddTaskModal && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4" onClick={() => setShowAddTaskModal(false)}>
              <div 
                className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-md`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Add Task for {card?.title}</h3>
                  <button onClick={() => setShowAddTaskModal(false)} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}>
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="p-4 space-y-4">
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Title *</label>
                    <input
                      type="text"
                      value={newTaskData.title}
                      onChange={(e) => setNewTaskData({ ...newTaskData, title: e.target.value })}
                      placeholder="Task title..."
                      className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Type</label>
                      <select
                        value={newTaskData.task_type}
                        onChange={(e) => setNewTaskData({ ...newTaskData, task_type: e.target.value })}
                        className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
                      >
                        <option value="call">Call</option>
                        <option value="email">Email</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="meeting">Meeting</option>
                        <option value="follow_up">Follow-up</option>
                        <option value="review">Review</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Priority</label>
                      <select
                        value={newTaskData.priority}
                        onChange={(e) => setNewTaskData({ ...newTaskData, priority: e.target.value })}
                        className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
                      >
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Assign To</label>
                    <select
                      value={newTaskData.assigned_to_id}
                      onChange={(e) => setNewTaskData({ ...newTaskData, assigned_to_id: e.target.value })}
                      className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
                    >
                      <option value="">Myself ({currentUser?.name || 'Current User'})</option>
                      {users.filter(u => u.user_id !== currentUser?.user_id).map(user => (
                        <option key={user.user_id} value={user.user_id}>{user.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Due Date</label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none flex items-center justify-between text-left`}
                          >
                            <span className={newTaskData.due_date ? '' : (isDark ? 'text-gray-500' : 'text-gray-400')}>
                              {newTaskData.due_date ? format(new Date(newTaskData.due_date + 'T00:00:00'), 'dd MMM yyyy') : 'Select date'}
                            </span>
                            <CalendarIcon className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className={`w-auto p-0 ${isDark ? 'bg-[#111] border-white/20' : 'bg-white border-gray-200'}`} align="start">
                          <CalendarComponent
                            mode="single"
                            selected={newTaskData.due_date ? new Date(newTaskData.due_date + 'T00:00:00') : undefined}
                            onSelect={(date) => {
                              if (date) {
                                const formattedDate = format(date, 'yyyy-MM-dd');
                                setNewTaskData({ ...newTaskData, due_date: formattedDate });
                              } else {
                                setNewTaskData({ ...newTaskData, due_date: '' });
                              }
                            }}
                            className={isDark ? 'bg-[#111] text-white' : 'bg-white text-gray-900'}
                            classNames={!isDark ? {
                              months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                              month: "space-y-4",
                              caption: "flex justify-center pt-1 relative items-center",
                              caption_label: "text-sm font-medium text-gray-900",
                              nav: "space-x-1 flex items-center",
                              nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 border border-gray-300 rounded-md hover:bg-gray-100",
                              nav_button_previous: "absolute left-1",
                              nav_button_next: "absolute right-1",
                              table: "w-full border-collapse space-y-1",
                              head_row: "flex",
                              head_cell: "text-gray-500 rounded-md w-8 font-normal text-[0.8rem]",
                              row: "flex w-full mt-2",
                              cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-emerald-100 [&:has([aria-selected])]:rounded-md",
                              day: "h-8 w-8 p-0 font-normal aria-selected:opacity-100 hover:bg-gray-100 rounded-md text-gray-900",
                              day_selected: "bg-emerald-500 text-white hover:bg-emerald-600 hover:text-white focus:bg-emerald-500 focus:text-white",
                              day_today: "bg-gray-100 text-gray-900",
                              day_outside: "text-gray-400 aria-selected:bg-emerald-50 aria-selected:text-gray-500",
                              day_disabled: "text-gray-300",
                              day_hidden: "invisible",
                            } : undefined}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Time</label>
                      <input
                        type="time"
                        value={newTaskData.due_time}
                        onChange={(e) => setNewTaskData({ ...newTaskData, due_time: e.target.value })}
                        className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Description</label>
                    <textarea
                      value={newTaskData.description}
                      onChange={(e) => setNewTaskData({ ...newTaskData, description: e.target.value })}
                      placeholder="Task details..."
                      rows={3}
                      className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
                    />
                  </div>
                </div>
                
                <div className={`flex gap-3 p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <button
                    onClick={() => setShowAddTaskModal(false)}
                    className={`flex-1 px-4 py-2 rounded-sm ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddTask}
                    disabled={savingTask || !newTaskData.title.trim()}
                    className={`flex-1 px-4 py-2 rounded-sm ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-emerald-500 hover:bg-emerald-600'} text-black font-bold disabled:opacity-50`}
                  >
                    {savingTask ? 'Creating...' : 'Create Task'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Proposals Tab */}
          {activeTab === 'proposals' && isMemberTracking && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Proposals</h3>
                <div className="flex items-center gap-2">
                  {proposalTemplates.length > 0 && (
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          createProposal(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      disabled={creatingProposal}
                      className={`${isDark ? 'bg-[#A2FF00]' : 'bg-emerald-500'} text-black font-bold px-4 py-2 rounded-sm text-sm cursor-pointer`}
                    >
                      <option value="">{creatingProposal ? 'Creating...' : '+ Create Proposal'}</option>
                      {proposalTemplates.map(t => (
                        <option key={t.template_id} value={t.template_id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {loadingProposals ? (
                <div className="text-center py-8">
                  <p className="text-gray-400">Loading proposals...</p>
                </div>
              ) : cardProposals.length === 0 ? (
                <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-8 text-center`}>
                  <FileSignature className={`w-12 h-12 ${isDark ? 'text-gray-600' : 'text-gray-400'} mx-auto mb-4`} />
                  <h4 className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium mb-2`}>No proposals yet</h4>
                  <p className={`${isDark ? 'text-gray-500' : 'text-gray-600'} text-sm mb-4`}>
                    Create a proposal to send to {editedCard?.title || 'this company'}.
                  </p>
                  {proposalTemplates.length === 0 && (
                    <p className="text-yellow-500 text-xs">
                      No templates available. Create a template first.
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {cardProposals.map((proposal) => {
                    const statusColors = {
                      draft: 'text-gray-400 bg-gray-500/20',
                      sent: 'text-blue-400 bg-blue-500/20',
                      viewed: 'text-yellow-400 bg-yellow-500/20',
                      signed: 'text-green-400 bg-green-500/20',
                      expired: 'text-red-400 bg-red-500/20'
                    };
                    const statusColor = statusColors[proposal.status] || statusColors.draft;
                    
                    return (
                      <div 
                        key={proposal.proposal_id}
                        className={`${isDark ? 'bg-[#111] border-white/10 hover:border-white/30' : 'bg-gray-50 border-gray-200 hover:border-gray-400'} border rounded-sm p-4 transition-colors`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <FileSignature className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'}`} />
                              <span className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>{proposal.title}</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${statusColor}`}>
                                {proposal.status}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-400">
                              <span>Created: {new Date(proposal.created_at).toLocaleDateString()}</span>
                              {proposal.pricing?.total_gbp > 0 && (
                                <span className={`${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'}`}>
                                  £{proposal.pricing.total_gbp.toLocaleString()}
                                </span>
                              )}
                              {proposal.tracking?.open_count > 0 && (
                                <span className="flex items-center gap-1">
                                  <Eye className="w-3 h-3" />
                                  {proposal.tracking.open_count} views
                                </span>
                              )}
                              {proposal.signature && (
                                <span className="flex items-center gap-1 text-green-400">
                                  <CheckCircle className="w-3 h-3" />
                                  Signed
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {proposal.status === 'draft' ? (
                              <button
                                onClick={() => {
                                  onClose();
                                  navigate(`/proposals/${proposal.proposal_id}/edit`);
                                }}
                                className={`px-3 py-1.5 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-emerald-500 hover:bg-emerald-600'} text-black text-sm font-bold rounded-sm transition-colors`}
                              >
                                Edit
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    onClose();
                                    navigate(`/proposals/${proposal.proposal_id}`);
                                  }}
                                  className={`px-3 py-1.5 ${isDark ? 'bg-[#111] border-white/20 text-white hover:border-[#A2FF00]' : 'bg-white border-gray-300 text-gray-900 hover:border-emerald-500'} border text-sm rounded-sm transition-colors`}
                                >
                                  View
                                </button>
                                <a
                                  href={`/p/${proposal.access_token}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`p-1.5 ${isDark ? 'text-gray-400 hover:text-[#A2FF00] hover:bg-[#A2FF00]/10' : 'text-gray-500 hover:text-emerald-600 hover:bg-emerald-50'} rounded-sm transition-colors`}
                                  title="Open Proposal"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Operations Tab */}
          {activeTab === 'operations' && isMemberTracking && (
            <div className="space-y-6">
              <OperationsChecklist
                card={editedCard}
                onUpdate={onUpdate}
              />
            </div>
          )}

          {/* Media Tab */}
          {activeTab === 'media' && isMemberTracking && (
            <div className="space-y-6">
              <MediaChecklist
                card={editedCard}
                onUpdate={onUpdate}
                onTabChange={setActiveTab}
                eventDate={editedCard?.event_date}
              />
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && isMemberTracking && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Card History</h3>
                <button
                  onClick={fetchCardHistory}
                  className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#A2FF00] hover:text-[#8EFF00]' : 'text-emerald-600 hover:text-emerald-700'}`}
                >
                  <RefreshCw className={`w-4 h-4 ${loadingHistory ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              {/* Retirement Status Banner */}
              {card.is_retired && (
                <div className="bg-orange-500/20 border border-orange-500/30 rounded-sm p-4">
                  <div className="flex items-center gap-3">
                    <Archive className="w-5 h-5 text-orange-400" />
                    <div>
                      <div className="text-orange-400 font-medium">This card is retired</div>
                      <div className="text-sm text-gray-400">
                        Reason: {(card.retired_reason || 'unknown').replace(/_/g, ' ')}
                        {card.retired_at && ` • ${new Date(card.retired_at).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}`}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {loadingHistory ? (
                <div className={`${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'}`}>Loading history...</div>
              ) : cardHistory.length === 0 ? (
                <div className="text-gray-500 text-center py-8">
                  No history records yet. Click &quot;Refresh&quot; to load history.
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {cardHistory.map((entry, index) => (
                    <div key={entry.audit_id || index} className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-3`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <span className={`${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} font-medium capitalize`}>
                            {entry.changed_field?.replace(/_/g, ' ')}
                          </span>
                          <span className="text-gray-500 text-sm ml-2">changed</span>
                        </div>
                        <span className="text-gray-500 text-xs">
                          {new Date(entry.changed_at).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div className="mt-2 text-sm">
                        {entry.old_value && (
                          <div className="text-red-400/70">
                            <span className="text-gray-500">From: </span>
                            {entry.old_value}
                          </div>
                        )}
                        {entry.new_value && (
                          <div className="text-green-400/70">
                            <span className="text-gray-500">To: </span>
                            {entry.new_value}
                          </div>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        by {entry.changed_by_name || 'Unknown'}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Card Metadata */}
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-4 mt-4`}>
                <h4 className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Card Information</h4>
                <div className="text-sm space-y-1">
                  <div><span className="text-gray-500">Card ID:</span> <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{card.card_id}</span></div>
                  <div><span className="text-gray-500">Created:</span> <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{card.created_at ? new Date(card.created_at).toLocaleDateString('en-GB') : 'Unknown'}</span></div>
                  <div><span className="text-gray-500">Last Updated:</span> <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{card.updated_at ? new Date(card.updated_at).toLocaleDateString('en-GB') : 'Unknown'}</span></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Retire Modal */}
        {showRetireModal && (
          <div className={`absolute inset-0 ${isDark ? 'bg-black/90' : 'bg-black/50'} flex items-center justify-center z-10 p-4`}>
            <div className={`${isDark ? 'bg-[#0A0A0A] border-white/20' : 'bg-white border-gray-200'} border rounded-sm p-6 max-w-md w-full`}>
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-orange-400" />
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Retire Card</h3>
              </div>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                This will move &quot;{card.title}&quot; to the Companies database. Select a reason:
              </p>
              <select
                value={retireReason}
                onChange={(e) => setRetireReason(e.target.value)}
                className={`w-full ${isDark ? 'bg-black border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm p-3 mb-4 focus:border-[#A2FF00]`}
              >
                <option value="">Select reason...</option>
                <option value="rejected">Rejected</option>
                <option value="no_budget">No Budget</option>
                <option value="not_right_time">Not the Right Time</option>
                <option value="lost_to_competitor">Lost to Competitor</option>
                <option value="no_response">No Response</option>
                <option value="project_cancelled">Project Cancelled</option>
                <option value="other">Other</option>
              </select>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowRetireModal(false)}
                  className={`px-4 py-2 border ${isDark ? 'border-white/20 text-white hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} rounded-sm`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRetire}
                  disabled={retiring || !retireReason}
                  className="px-4 py-2 bg-orange-500 text-white rounded-sm hover:bg-orange-600 disabled:opacity-50"
                >
                  {retiring ? 'Retiring...' : 'Retire Card'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reassign Modal */}
        {showReassignModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4" onClick={() => setShowReassignModal(false)}>
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6 max-w-md w-full`} onClick={e => e.stopPropagation()}>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
                <UserCheck className="w-5 h-5 text-blue-400" />
                Reassign Card
              </h3>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm mb-4`}>
                Select a team member to reassign this card to. They will become the new relationship owner.
              </p>
              <div className="mb-4">
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                  New Owner
                </label>
                <select
                  value={reassignUserId}
                  onChange={(e) => setReassignUserId(e.target.value)}
                  className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm p-3 focus:border-blue-500 outline-none`}
                >
                  <option value="">Select a team member...</option>
                  {users.map(user => (
                    <option key={user.user_id} value={user.user_id}>
                      {user.name} {user.user_id === editedCard?.relationship_owner_id ? '(Current)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowReassignModal(false);
                    setReassignUserId('');
                  }}
                  className={`px-4 py-2 ${isDark ? 'border-white/20 text-white hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} border rounded-sm`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReassign}
                  disabled={reassigning || !reassignUserId}
                  className="px-4 py-2 bg-blue-500 text-white rounded-sm hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
                >
                  <UserCheck className="w-4 h-4" />
                  {reassigning ? 'Reassigning...' : 'Reassign Card'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions - Fixed at bottom */}
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border-t p-6 flex items-center justify-between flex-shrink-0`}>
          <div className="flex items-center gap-2">
            {/* Reassign Button - Always visible */}
            <button
              onClick={() => setShowReassignModal(true)}
              className={`px-4 py-2 ${isDark ? 'border-blue-500 text-blue-400 hover:bg-blue-500/10' : 'border-blue-500 text-blue-600 hover:bg-blue-50'} border rounded-sm flex items-center gap-2`}
            >
              <UserCheck className="w-4 h-4" />
              Reassign
            </button>
            {/* Retire / Reinstate buttons */}
            {isMemberTracking && card?.is_retired ? (
              <button
                onClick={handleReinstate}
                disabled={reinstating}
                className="px-4 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${reinstating ? 'animate-spin' : ''}`} />
                {reinstating ? 'Reinstating...' : 'Reinstate Card'}
              </button>
            ) : canRetire ? (
              <button
                onClick={() => setShowRetireModal(true)}
                className="px-4 py-2 border border-orange-500 text-orange-400 rounded-sm hover:bg-orange-500/10 flex items-center gap-2"
              >
                <Archive className="w-4 h-4" />
                Retire Card
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className={`px-6 py-2 border ${isDark ? 'border-white/20 text-white hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} rounded-sm transition-colors`}
            >
              Cancel
            </button>
            {/* Auto-save status indicator */}
            <div className={`flex items-center gap-2 px-4 py-2 rounded-sm ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
              {saveStatus === 'saving' && (
                <>
                  <div className={`w-4 h-4 border-2 ${isDark ? 'border-[#A2FF00]' : 'border-emerald-600'} border-t-transparent rounded-full animate-spin`} />
                  <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Saving...</span>
                </>
              )}
              {saveStatus === 'saved' && (
                <>
                  <CheckCircle className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'}`} />
                  <span className={`${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} text-sm`}>Saved</span>
                </>
              )}
              {saveStatus === 'error' && (
                <>
                  <AlertCircle className="w-4 h-4 text-red-400" />
                  <span className="text-red-400 text-sm">Error saving</span>
                </>
              )}
              {saveStatus === 'idle' && (
                <>
                  <CheckCircle className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-500 text-sm">Auto-save on</span>
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className={`px-6 py-2 ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} font-bold rounded-sm transition-colors flex items-center gap-2`}
            >
              <X className="w-4 h-4" />
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Speaker Invite Modal */}
      {showSpeakerInviteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]">
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-2xl max-h-[80vh] overflow-hidden`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Send Speaker Confirmation Emails
              </h3>
              <button
                onClick={() => setShowSpeakerInviteModal(false)}
                className={`p-2 ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'} rounded-sm`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {speakerInviteLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className={`w-8 h-8 border-2 ${isDark ? 'border-[#A2FF00]' : 'border-emerald-600'} border-t-transparent rounded-full animate-spin`} />
                </div>
              ) : speakerInvitePreview ? (
                <div className="space-y-4">
                  {/* Speaker Selection */}
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                      Select Speakers to Send To:
                    </label>
                    <div className="space-y-2">
                      {(editedCard.speakers || []).map((speaker, idx) => (
                        speaker.email && (
                          <label key={idx} className={`flex items-center gap-3 p-3 rounded-sm cursor-pointer ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'}`}>
                            <input
                              type="checkbox"
                              checked={selectedSpeakers.includes(idx)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedSpeakers([...selectedSpeakers, idx]);
                                } else {
                                  setSelectedSpeakers(selectedSpeakers.filter(i => i !== idx));
                                }
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-[#A2FF00] focus:ring-[#A2FF00]"
                            />
                            <div>
                              <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {speaker.name || `Speaker ${idx + 1}`}
                              </div>
                              <div className="text-sm text-gray-500">{speaker.email}</div>
                              {speaker.invite_sent_at && (
                                <div className="text-xs text-green-500 mt-1">
                                  ✓ Previously sent on {new Date(speaker.invite_sent_at).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          </label>
                        )
                      ))}
                    </div>
                  </div>
                  
                  {/* Email Preview */}
                  <div className={`border ${isDark ? 'border-white/10' : 'border-gray-200'} rounded-sm p-4`}>
                    <div className="mb-3">
                      <label className={`block text-xs font-medium ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-1`}>Subject:</label>
                      <input
                        type="text"
                        value={speakerInvitePreview.subject}
                        onChange={(e) => setSpeakerInvitePreview({ ...speakerInvitePreview, subject: e.target.value })}
                        className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm p-2 text-sm`}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-1`}>Email Body:</label>
                      <div 
                        className={`${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm p-3 text-sm prose prose-sm max-w-none ${isDark ? 'prose-invert' : ''}`}
                        dangerouslySetInnerHTML={{ __html: sanitizeHTML(speakerInvitePreview.body) }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Note: Speaker name will be personalized for each recipient
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Failed to load email template
                </div>
              )}
            </div>
            
            <div className={`flex items-center justify-end gap-3 p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowSpeakerInviteModal(false)}
                className={`px-4 py-2 ${isDark ? 'border-white/20 text-white hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} border rounded-sm`}
              >
                Cancel
              </button>
              <button
                onClick={sendSpeakerInvites}
                disabled={speakerInviteSending || selectedSpeakers.length === 0}
                className={`px-4 py-2 rounded-sm font-medium flex items-center gap-2 ${
                  selectedSpeakers.length > 0 && !speakerInviteSending
                    ? 'bg-[#A2FF00] text-black hover:bg-[#8FE500]'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                {speakerInviteSending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send to {selectedSpeakers.length} Speaker{selectedSpeakers.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardDetailModal;
